import {
  fallbackText,
  getDataFromLink,
  parseTariff,
  isEmptyText,
  Prismic,
  PrismicLink,
  PrismicText,
} from '@landsbankinn/prismic';
import { ArrowButton, Segment } from '@landsbankinn/ui';
import React from 'react';
import { PointCard } from '../components/card/PointCard';
import { TextWithPoints } from '../components/slices/TextWithPoints';
import {
  ListItem,
  TextWithPointsPage,
} from '../components/slices/TextWithPointsPage';

interface IProps {
  title?: Prismic.RichText;
  title_size?: 'Medium' | 'Big' | 'Small';
  copy: Prismic.RichText;
  copy_size?: boolean;
  link?: Prismic.Link;
  link_text?: Prismic.RichText;
  points_type?: 'Empty' | 'Bullets' | 'Numbers' | 'Text' | 'Icon';
  points_color?: 'Default' | 'Auto' | 'Blue' | 'LightBlue' | 'Cyan' | 'Orange';
  line_splitter?: boolean;
  columns_count?: 'Auto' | '2' | '3' | '4' | '5' | '6';
  points_location?: boolean;
  fields: IField[];
  list_style?: 'Unordered Lists' | 'Ordered Lists';
  template: string;
  point_copy_size?: boolean;
  isArticle?: boolean;
}

interface IField {
  copy: Prismic.RichText;
  icon?: 'None' | 'House' | 'Money';
  label?: Prismic.RichText;
  isSplitterLong?: boolean;
  cols?: number;
  number?: number;
  pointColor?: string;
  pointsType?: string;
  isBottomAligned?: boolean;
  isBodySize?: boolean;
  point_link: Prismic.Link;
}

const renderPoints = ({ copy, point_link }: IField, i: number) => {
  return (
    <React.Fragment key={`${i}-${Prismic.text(copy)}`}>
      {point_link && (
        <PrismicLink value={point_link}>
          <a>
            <ListItem content={parseTariff(Prismic.text(copy))} />
          </a>
        </PrismicLink>
      )}
      {!point_link && <ListItem content={parseTariff(Prismic.text(copy))} />}
    </React.Fragment>
  );
};

const CardItem = ({
  copy,
  icon,
  label,
  isSplitterLong,
  cols,
  pointColor,
  number,
  pointsType,
  isBottomAligned,
  isBodySize,
  point_link,
  ...rest
}: IField) => {
  return (
    <PointCard
      icon={icon}
      copy={parseTariff(Prismic.text(copy))}
      label={Prismic.text(label) ?? undefined}
      isSplitterLong={isSplitterLong}
      cols={cols}
      pointColor={pointColor}
      number={number}
      pointsType={pointsType}
      isBottomAligned={isBottomAligned}
      isBodySize={isBodySize}
      pointLink={point_link}
      {...rest}
    />
  );
};

export const TextWithPointsContainer = ({
  title,
  title_size,
  copy,
  copy_size,
  link,
  link_text,
  points_type,
  points_color,
  line_splitter: isSplitterLong,
  columns_count,
  points_location: isBottomAligned,
  fields,
  list_style,
  template,
  point_copy_size,
  isArticle,
}: IProps) => {
  const pointsCount = fields?.length;

  const copyText = isEmptyText(copy) ? null : (
    <PrismicText value={copy} isArticle={isArticle} />
  );

  const linkdata = getDataFromLink(link);
  const linkText = fallbackText(link_text as any, linkdata.title ?? undefined);

  const action = link ? (
    <PrismicLink value={link} title={linkText ?? undefined}>
      <ArrowButton title={linkText ?? ''} isSmall />
    </PrismicLink>
  ) : null;

  if (isArticle) {
    return (
      <Segment isArticle={isArticle}>
        <TextWithPoints
          copy={copyText}
          boxTitle={Prismic.text(title)}
          listStyle={list_style}
          isArticle={isArticle}
        >
          {fields?.map(renderPoints)}
        </TextWithPoints>
      </Segment>
    );
  }
  const noTop = isEmptyText(title) && isEmptyText(copy);

  return (
    <Segment className="text-points">
      <TextWithPointsPage
        title={title}
        titleSize={title_size}
        copy={copyText}
        copySize={copy_size}
        action={action}
        pointsColor={points_color}
        pointsLocation={isBottomAligned}
        pointsType={points_type}
        noTop={noTop}
      >
        {points_type === 'Bullets' && !isBottomAligned
          ? fields?.map(renderPoints)
          : fields?.map((item, index) => {
              const colCount = !isBottomAligned
                ? 6
                : columns_count !== 'Auto'
                ? 12 / Number(columns_count)
                : pointsCount >= 6
                ? 2
                : 12 / pointsCount;
              return (
                <CardItem
                  key={`${index}-${Prismic.text(item.copy)}`}
                  isSplitterLong={isSplitterLong}
                  cols={colCount}
                  pointColor={points_color}
                  number={index + 1}
                  pointsType={points_type}
                  isBottomAligned={isBottomAligned}
                  isBodySize={point_copy_size}
                  {...item}
                />
              );
            })}
      </TextWithPointsPage>
    </Segment>
  );
};
